import { useEffect, useState } from 'react';
import { ehIOS, ehAndroid } from '@/lib/navegador';
import {
  suportaAvisos,
  estadoPermissao,
  situacaoAvisos,
  ligarAvisosGerais,
  testarAviso,
  type EstadoPermissao,
} from '@/lib/avisos';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Botao from '@/components/ui/Botao';

// ---------------------------------------------------------------------------
// Item "Avisos no celular" no perfil. Mostra o estado (ligados/desligados),
// o botão "Testar aviso" e, quando a permissão está negada, o passo a passo
// para ligar nas configurações do aparelho.
// ---------------------------------------------------------------------------
export default function AvisosCelular() {
  const [carregando, setCarregando] = useState(true);
  const [ativa, setAtiva] = useState(false);
  const [permissao, setPermissao] = useState<EstadoPermissao>('unsupported');
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const perm = estadoPermissao();
      const sit = await situacaoAvisos();
      if (!vivo) return;
      setPermissao(perm);
      setAtiva(perm === 'granted' && sit.ativa);
      setCarregando(false);
    })();
    return () => {
      vivo = false;
    };
  }, []);

  async function ativar() {
    setOcupado(true);
    try {
      await ligarAvisosGerais();
      setPermissao('granted');
      setAtiva(true);
      mostrarToast('Avisos ligados! Você recebe no celular.', 5000, 'sucesso');
    } catch (e) {
      setPermissao(estadoPermissao());
      mostrarToast(mensagemAmigavel(e, 'Não conseguimos ligar os avisos.'), 6000, 'erro');
    } finally {
      setOcupado(false);
    }
  }

  async function testar() {
    setOcupado(true);
    try {
      const r = await testarAviso();
      if (r.ok) {
        mostrarToast('Enviado! Deve chegar em alguns segundos.', 5000, 'sucesso');
      } else {
        mostrarToast(r.erro || 'Não foi possível enviar o teste agora.', 6000, 'aviso');
      }
    } catch (e) {
      mostrarToast(mensagemAmigavel(e, 'Não foi possível enviar o teste agora.'), 6000, 'erro');
    } finally {
      setOcupado(false);
    }
  }

  if (carregando) {
    return <div className="h-[88px] w-full animate-pulse rounded-lg bg-secondary-100" />;
  }

  if (!suportaAvisos()) {
    return (
      <div className="rounded-lg border border-secondary-100 bg-white p-4">
        <p className="text-sm text-foreground-600">
          Este aparelho não recebe avisos do Se Liga. No iPhone, use o app na tela inicial.
        </p>
      </div>
    );
  }

  const negado = permissao === 'denied';

  return (
    <div className="rounded-lg border border-secondary-100 bg-white p-4">
      <div className="flex items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            ativa ? 'bg-success-100 text-success-700' : 'bg-background-100 text-foreground-500'
          }`}
        >
          <i className={ativa ? 'ri-notification-3-fill' : 'ri-notification-off-line'} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground-950">Avisos no celular</p>
          <p className={`mt-0.5 text-sm ${ativa ? 'text-success-700' : 'text-foreground-600'}`}>
            {ativa ? 'Ligados' : 'Desligados'}
          </p>
        </div>
      </div>

      {negado ? (
        <div className="mt-3 rounded-lg bg-background-50 p-3">
          <p className="text-sm text-foreground-700">
            Para ligar os avisos, autorize o Se Liga nas configurações do celular:
          </p>
          <ol className="mt-2 space-y-1.5 text-sm text-foreground-600">
            {ehIOS ? (
              <>
                <li>1. Ajustes → Notificações.</li>
                <li>2. Toque em “Se Liga”.</li>
                <li>3. Ligue “Permitir notificações”.</li>
              </>
            ) : ehAndroid ? (
              <>
                <li>1. Configurações → Apps.</li>
                <li>2. Toque em “Se Liga” → Notificações.</li>
                <li>3. Ligue as notificações.</li>
              </>
            ) : (
              <>
                <li>1. Abra as configurações do navegador/sistema.</li>
                <li>2. Vá em Notificações.</li>
                <li>3. Autorize o Se Liga.</li>
              </>
            )}
          </ol>
        </div>
      ) : (
        <div className="mt-3">
          {ativa ? (
            <Botao
              variante="secundario"
              tamanho="md"
              icone="ri-send-plane-line"
              carregando={ocupado}
              onClick={testar}
              className="w-full sm:w-auto"
            >
              Testar aviso
            </Botao>
          ) : (
            <Botao
              variante="primario"
              tamanho="md"
              icone="ri-notification-3-line"
              carregando={ocupado}
              onClick={ativar}
              className="w-full sm:w-auto"
            >
              Ativar avisos
            </Botao>
          )}
        </div>
      )}
    </div>
  );
}